import React, { useState, useEffect } from "react";
import { Card, Form, Button, Alert, Spinner, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";

// Styles supplémentaires
const styles = `
  .edit-profile-container {
    max-width: 800px;
    margin: 0 auto;
  }

  .edit-profile-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 20px;
    padding: 40px;
    color: white;
    margin-bottom: 30px;
    box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
  }

  .edit-profile-card {
    border: none;
    border-radius: 20px;
    background: white;
    box-shadow: 0 10px 40px rgba(0,0,0,0.08);
    overflow: hidden;
  }

  .edit-profile-card:hover {
    box-shadow: 0 15px 50px rgba(0,0,0,0.12);
  }

  .edit-profile-form {
    padding: 30px;
  }

  .form-label-custom {
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 8px;
    font-size: 14px;
  }

  .form-control-custom {
    border: 2px solid #e9ecef;
    border-radius: 12px;
    padding: 12px 16px;
    transition: all 0.3s ease;
    font-size: 15px;
  }

  .form-control-custom:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    outline: none;
  }

  .btn-gradient {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    padding: 12px 30px;
    font-weight: 600;
    border-radius: 12px;
    transition: all 0.3s ease;
    color: white;
  }

  .btn-gradient:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(102, 126, 234, 0.4);
  }

  .btn-gradient:disabled {
    opacity: 0.7;
    transform: none;
  }

  .btn-outline-custom {
    background: transparent;
    border: 2px solid #667eea;
    color: #667eea;
    padding: 12px 30px;
    font-weight: 600;
    border-radius: 12px;
    transition: all 0.3s ease;
  }

  .btn-outline-custom:hover {
    background: #667eea;
    color: white;
    transform: translateY(-2px);
  }

  .alert-custom {
    border-radius: 15px;
    border: none;
    padding: 15px 20px;
  }

  .avatar-preview {
    width: 100px;
    height: 100px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 36px;
    font-weight: bold;
    margin: 0 auto 20px;
    border: 4px solid white;
    box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-fadeInUp {
    animation: fadeInUp 0.6s ease forwards;
  }
`;

export default function EditProfile() {
    const navigate = useNavigate();
    const [user, setUser] = useState({
        nom_utilisateur: '',
        prenom_utilisateur: '',
        email: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        fetchProfile();
    }, []);

    // ✅ Récupérer l'utilisateur depuis localStorage (sans token)
    const getCurrentUser = () => {
        try {
            const userStr = localStorage.getItem("user");
            if (userStr && userStr !== "undefined" && userStr !== "null") {
                return JSON.parse(userStr);
            }
        } catch (error) {
            console.error("Erreur parsing utilisateur:", error);
            localStorage.removeItem("user");
        }
        return null;
    };

    const fetchProfile = async () => {
        try {
            setLoading(true);
            
            const currentUser = getCurrentUser();
            
            if (!currentUser) {
                navigate('/pages/signin2');
                return;
            }

            const userId = currentUser.id_utilisateur || currentUser.id;
            
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/utilisateurs.php?action=profil&user_id=${userId}`,
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );
            
            if (!response.ok) {
                throw new Error(`Erreur HTTP: ${response.status}`);
            }
            
            const result = await response.json();
            
            if (result.success) {
                setUser(result.data);
            } else {
                setError(result.message);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setUser(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        setSuccess(false);

        try {
            const currentUser = getCurrentUser();
            const userId = currentUser.id_utilisateur || currentUser.id;
            
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/update_profil.php?user_id=${userId}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(user)
                }
            );
            
            if (!response.ok) {
                throw new Error(`Erreur HTTP: ${response.status}`);
            }
            
            const result = await response.json();
            
            if (result.success) {
                // Mettre à jour le localStorage avec les nouvelles données
                const updatedUser = { ...currentUser, ...user };
                localStorage.setItem("user", JSON.stringify(updatedUser));
                
                setSuccess(true);
                setTimeout(() => {
                    navigate('/pages/profile');
                }, 2000);
            } else {
                setError(result.message || "Erreur lors de la mise à jour");
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    // Obtenir les initiales pour l'avatar
    const getUserInitials = () => {
        const prenom = user.prenom_utilisateur || '';
        const nom = user.nom_utilisateur || '';
        return `${prenom.charAt(0) || ''}${nom.charAt(0) || ''}`.toUpperCase() || 'U';
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="text-center py-5">
                        <div className="spinner-grow text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                            <span className="visually-hidden">Chargement...</span>
                        </div>
                        <p className="mt-3 text-muted">Chargement de vos informations...</p>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <style>{styles}</style>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="edit-profile-container">
                    {/* En-tête avec dégradé */}
                    <div className="edit-profile-header animate-fadeInUp">
                        <h1 className="display-5 fw-bold mb-2">
                            <i className="ri-edit-line me-3"></i>
                            Modifier le profil
                        </h1>
                        <p className="lead mb-0 opacity-75">
                            Mettez à jour vos informations personnelles
                        </p>
                    </div>

                    {/* Carte principale */}
                    <Card className="edit-profile-card animate-fadeInUp">
                        <Card.Body className="p-0">
                            <div className="edit-profile-form">
                                {/* Avatar preview */}
                                <div className="text-center mb-4">
                                    <div className="avatar-preview">
                                        {getUserInitials()}
                                    </div>
                                    <p className="text-muted mb-0">Aperçu de votre avatar</p>
                                </div>

                                {/* Alertes */}
                                {error && (
                                    <Alert 
                                        variant="danger" 
                                        className="alert-custom mb-4"
                                        onClose={() => setError(null)} 
                                        dismissible
                                    >
                                        <div className="d-flex align-items-center">
                                            <i className="ri-error-warning-line fs-4 me-3"></i>
                                            <div>{error}</div>
                                        </div>
                                    </Alert>
                                )}
                                
                                {success && (
                                    <Alert 
                                        variant="success" 
                                        className="alert-custom mb-4"
                                    >
                                        <div className="d-flex align-items-center">
                                            <i className="ri-checkbox-circle-line fs-4 me-3"></i>
                                            <div>
                                                <strong>Profil mis à jour avec succès !</strong>
                                                <br />
                                                Redirection vers votre profil...
                                            </div>
                                        </div>
                                    </Alert>
                                )}

                                <Form onSubmit={handleSubmit}>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-4">
                                                <Form.Label className="form-label-custom">
                                                    <i className="ri-user-line me-2 text-primary"></i>
                                                    Nom
                                                </Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="nom_utilisateur"
                                                    value={user.nom_utilisateur}
                                                    onChange={handleChange}
                                                    required
                                                    className="form-control-custom"
                                                    placeholder="Votre nom"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-4">
                                                <Form.Label className="form-label-custom">
                                                    <i className="ri-user-smile-line me-2 text-primary"></i>
                                                    Prénom
                                                </Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="prenom_utilisateur"
                                                    value={user.prenom_utilisateur}
                                                    onChange={handleChange}
                                                    required
                                                    className="form-control-custom"
                                                    placeholder="Votre prénom"
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Form.Group className="mb-5">
                                        <Form.Label className="form-label-custom">
                                            <i className="ri-mail-line me-2 text-primary"></i>
                                            Email
                                        </Form.Label>
                                        <Form.Control
                                            type="email"
                                            name="email"
                                            value={user.email}
                                            onChange={handleChange}
                                            required
                                            className="form-control-custom"
                                            placeholder="votre@email.com"
                                        />
                                        <Form.Text className="text-muted">
                                            Nous ne partagerons jamais votre email.
                                        </Form.Text>
                                    </Form.Group>

                                    <div className="d-flex gap-3 justify-content-end">
                                      
                                        <Button 
                                            type="submit"
                                            disabled={saving}
                                            className="btn-gradient"
                                        >
                                            {saving ? (
                                                <>
                                                    <Spinner
                                                        as="span"
                                                        animation="border"
                                                        size="sm"
                                                        role="status"
                                                        aria-hidden="true"
                                                        className="me-2"
                                                    />
                                                    Enregistrement...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="ri-save-line me-2"></i>
                                                    Enregistrer les modifications
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </Form>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* Carte d'information complémentaire */}
                    <Card className="edit-profile-card mt-4 animate-fadeInUp">
                        <Card.Body className="p-4">
                            <h6 className="fw-bold mb-3">
                                <i className="ri-information-line me-2 text-primary"></i>
                                Conseils de sécurité
                            </h6>
                            <Row>
                                <Col md={4} className="mb-3 mb-md-0">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-primary bg-opacity-10 p-2 rounded-3 me-3">
                                            <i className="ri-shield-check-line text-primary"></i>
                                        </div>
                                        <small>Utilisez un email valide</small>
                                    </div>
                                </Col>
                                <Col md={4} className="mb-3 mb-md-0">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-primary bg-opacity-10 p-2 rounded-3 me-3">
                                            <i className="ri-lock-line text-primary"></i>
                                        </div>
                                        <small>Protégez vos informations</small>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="d-flex align-items-center">
                                        <div className="bg-primary bg-opacity-10 p-2 rounded-3 me-3">
                                            <i className="ri-check-line text-primary"></i>
                                        </div>
                                        <small>Vérifiez avant d'enregistrer</small>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>
        </>
    );
} 